import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { Container, Card, Spinner, Alert, Accordion, Row, Col, Table, Form, Button, Badge, Pagination } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Clipboard } from 'react-bootstrap-icons';

// 発注番号をハイフン区切りにフォーマットするヘルパー関数
const formatDisplayOrderNumber = (orderNumber) => {
  if (!orderNumber || orderNumber.length < 13) {
    return orderNumber;
  }
  const datePart = orderNumber.substring(0, 8);
  const dailyPart = orderNumber.substring(8, 11);
  const destPart = orderNumber.substring(11);
  return `${datePart}-${dailyPart}-${destPart}`;
};

// ★ 追加: YYYYMMDD形式の文字列をYYYY/MM/DD形式に変換するヘルパー関数
const formatShippingDate = (dateStr) => {
  if (!dateStr || dateStr.length !== 8) {
    return dateStr; // フォーマットが不正な場合は元の文字列を返す
  }
  const year = dateStr.substring(0, 4);
  const month = dateStr.substring(4, 6);
  const day = dateStr.substring(6, 8);
  return `${year}/${month}/${day}`;
};

// 注文ステータスの表示名と色を定義
const statusMap = {
  new: { text: '新規', bg: 'primary' },
  processed: { text: '受付済み', bg: 'success' },
  shipped: { text: '出荷準備完了', bg: 'danger' }
};
const getTrackingUrl = (carrier, trackingNumber) => {
  if (!carrier || !trackingNumber) return null;
  const encodedTrackingNumber = encodeURIComponent(trackingNumber);
  switch (carrier) {
    case 'yupri':
      return `https://www.post.japanpost.jp/receive/tracking/result.php?code=${encodedTrackingNumber}`;
    case 'istar':
      return `https://corp.fukutsu.co.jp/situation/tracking_no_search?tracking_no=${encodedTrackingNumber}`;
    default:
      return null;
  }
};

// ページネーションの表示を制御するカスタムコンポーネント
const CustomPagination = ({ currentPage, totalPages, paginate }) => {
  const maxPageButtons = 5;
  let startPage, endPage;

  if (totalPages <= maxPageButtons) {
    startPage = 1;
    endPage = totalPages;
  } else {
    const maxPagesBeforeCurrentPage = Math.floor(maxPageButtons / 2);
    const maxPagesAfterCurrentPage = Math.ceil(maxPageButtons / 2) - 1;
    if (currentPage <= maxPagesBeforeCurrentPage) {
      startPage = 1;
      endPage = maxPageButtons;
    } else if (currentPage + maxPagesAfterCurrentPage >= totalPages) {
      startPage = totalPages - maxPageButtons + 1;
      endPage = totalPages;
    } else {
      startPage = currentPage - maxPagesBeforeCurrentPage;
      endPage = currentPage + maxPagesAfterCurrentPage;
    }
  }

  const pageNumbers = [...Array((endPage - startPage) + 1)].map((_, i) => startPage + i);

  return (
    <Pagination className="justify-content-center mt-3">
      <Pagination.First
        onClick={() => paginate(1)}
        disabled={currentPage === 1}
      />
      <Pagination.Prev
        onClick={() => paginate(currentPage - 1)}
        disabled={currentPage === 1}
      />
      {
        startPage > 1 &&
        <Pagination.Ellipsis onClick={() => paginate(startPage - 1)} />
      }
      {pageNumbers.map(number => (
        <Pagination.Item
          key={number}
          active={number === currentPage}
          onClick={() => paginate(number)}
        >
          {number}
        </Pagination.Item>
      ))}
      {
        endPage <
        totalPages &&
        <Pagination.Ellipsis onClick={() => paginate(endPage + 1)} />
      }
      <Pagination.Next
        onClick={() => paginate(currentPage + 1)}
        disabled={currentPage === totalPages}
      />
      <Pagination.Last
        onClick={() => paginate(totalPages)}
        disabled={currentPage === totalPages}
      />
    </Pagination>
  );
};

function History() {
  const [orders, setOrders] = useState([]);
  const [usersMap, setUsersMap] = useState(new Map());
  // ★ 追加: companiesMapのstateを他のstateと一緒に定義
  const [companiesMap, setCompaniesMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    document.title = '全注文履歴 | My Order System';

    const fetchData = async () => {
      try {
        // ★ 修正: ユーザー情報と会社情報を並行して取得
        const [usersSnapshot, companiesSnapshot] = await Promise.all([
          getDocs(collection(db, 'users')),
          getDocs(collection(db, 'companies'))
        ]);

        const usersDataMap = new Map();
        usersSnapshot.forEach(doc => usersDataMap.set(doc.id, doc.data()));
        setUsersMap(usersDataMap);

        // ★ 追加: 取得した会社情報をstateにセット
        const companiesDataMap = new Map();
        companiesSnapshot.forEach(doc => companiesDataMap.set(doc.id, doc.data()));
        setCompaniesMap(companiesDataMap);

        const ordersCollectionRef = collection(db, 'orders');
        const q = query(ordersCollectionRef, orderBy('orderNumber', 'desc'));
        const data = await getDocs(q);
        const fetchedOrders = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
        setOrders(fetchedOrders);
      } catch (err) {
        setError('履歴データの取得に失敗しました。');
        console.error(err);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  const filteredOrders = useMemo(() => {
    return orders.filter(order => {
      const orderDate = order.orderedAt?.toDate();

      if (startDate && orderDate) {
        if (orderDate < new Date(startDate)) return false;
      }
      if (endDate && orderDate) {
        const endOfDay = new Date(endDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (orderDate > endOfDay) return false;
      }

      if (!searchTerm.trim()) return true;
      const lowercasedFilter = searchTerm.toLowerCase();
      const customerName = usersMap.get(order.orderedByUid)?.personName || '';
      const detailsToSearch = (order.invoiceDetails || order.propertyName || '').toLowerCase();

      const hasMatchingDestination = order.shippingDestinations.some(dest =>
        (dest.recipientName || '').toLowerCase().includes(lowercasedFilter) ||
        (dest.phoneNumber || '').toLowerCase().includes(lowercasedFilter) ||
        (dest.orderNumber || '').toLowerCase().includes(lowercasedFilter) ||
        (dest.shippingCompanyName || '').toLowerCase().includes(lowercasedFilter) ||
        (dest.shippingBranchName || '').toLowerCase().includes(lowercasedFilter) ||
        (dest.shippingDepartmentName || '').toLowerCase().includes(lowercasedFilter)
      );

      return (
        customerName.toLowerCase().includes(lowercasedFilter) ||
        detailsToSearch.includes(lowercasedFilter) ||
        hasMatchingDestination
      );
    });
  }, [orders, searchTerm, startDate, endDate, usersMap]);

  const allShippingDestinations = useMemo(() => {
    return filteredOrders.flatMap(order =>
      order.shippingDestinations.map(dest => ({
        ...order,
        shippingInfo: dest,
        uniqueKey: dest.orderNumber,
      }))
    );
  }, [filteredOrders]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = allShippingDestinations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(allShippingDestinations.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header
            as="h2"
            className="p-3"
          >
            全注文履歴
          </Card.Header>
          <Card.Body className="p-4">
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <Card className="mb-4 bg-light">
              <Card.Body>
                <h5 className="mb-3">
                  検索条件
                </h5>
                <Row className="g-2">
                  <Col md={12}>
                    <Form.Control
                      type="text"
                      placeholder="発注番号、発注者名、送付先会社名・氏名・電話番号、送り状の記載事項で検索..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </Col>
                  <Col md={5}>
                    <Form.Control
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </Col>
                  <Col md={5}>
                    <Form.Control
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </Col>
                  <Col md={2}>
                    <div className="d-grid">
                      <Button
                        variant="secondary"
                        onClick={() => { setSearchTerm(''); setStartDate(''); setEndDate(''); setCurrentPage(1); }}>
                        クリア
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            {
              loading ?
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
                :
                (
                  <>
                    <p>
                      {allShippingDestinations.length}件の送付先が見つかりました。
                    </p>
                    <Accordion>
                      {currentItems.map((item) => {
                        const { shippingInfo: dest, ...order } = item;
                        const invoiceDetailsText = order.invoiceDetails ||
                          `${order.propertyName || ''}${order.propertyName && order.remarks ? ' ' : ''}${order.remarks || ''}` ||
                          '(記載なし)';
                        const statusInfo = statusMap[order.status] || { text: order.status || '新規', bg: 'secondary' };
                        // ★ 追加: 発注者と会社の情報を取得・整形するロジック
                        const customer = usersMap.get(order.orderedByUid);
                        const company = customer ? companiesMap.get(customer.companyId) : null;
                        const trackingUrl = getTrackingUrl(order.shippingCarrier, dest.trackingNumber);

                        return (
                          <Accordion.Item
                            eventKey={dest.orderNumber}
                            key={dest.orderNumber}
                          >
                            <Accordion.Header>
                              <Container fluid>
                                <Row className="w-100 align-items-center g-2">
                                  <Col xs={12} md={5} className="text-center text-md-start">
                                    <div>
                                      発注No.
                                    </div>
                                    <span className="fw-bold fs-5">
                                      {formatDisplayOrderNumber(dest.orderNumber)}
                                    </span>
                                    <hr />
                                    <div>
                                      送り状No.
                                      <span>
                                        {
                                          order.shippingCarrier === 'yupri' &&
                                          <span className="badge" style={{ backgroundColor: '#CCCC66', color: 'black' }}>
                                            日本郵便
                                          </span>
                                        }
                                        {
                                          order.shippingCarrier === 'istar' &&
                                          <span className="badge" style={{ backgroundColor: '#FFCC33', color: 'black' }}>
                                            福山通運
                                          </span>
                                        }
                                      </span>
                                    </div>
                                    <span className="fw-bold fs-5">
                                      {
                                        dest.trackingNumber ?
                                          <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
                                            {dest.trackingNumber}
                                          </a>
                                          :
                                          'ー'
                                      }
                                    </span>
                                  </Col>
                                  <Col xs={12} md={6} className="text-center text-md-start">
                                    <div>
                                      <small className="text-muted">発注者会社名:</small>
                                      <span>
                                        {
                                          company ?
                                            `${company.companyPrefix || ''}${company.companyName}${company.companySuffix || ''}`
                                            :
                                            ''
                                        }
                                        {
                                          company ?
                                            `${company.branchName || ''} ${company.departmentName || ''}`.trim() || ''
                                            :
                                            ''
                                        }
                                      </span>
                                    </div>
                                    <div>
                                      <small className="text-muted">
                                        発注者:
                                      </small>
                                      <span>
                                        {customer?.personName || '不明'}
                                      </span>
                                    </div>
                                    <div>
                                      <small className="text-muted">
                                        発注日時:
                                      </small>
                                      <span>
                                        {order.orderedAt?.toDate().toLocaleString('ja-JP')}
                                      </span>
                                    </div>
                                    <div>
                                      <small className="text-muted">
                                        出荷予定日:
                                      </small>
                                      <strong>
                                        {formatShippingDate(order.estimatedShippingDate)}
                                      </strong>
                                    </div>
                                  </Col>
                                  <Col xs={12} md={1} className="text-center text-md-end">
                                    <Badge bg={statusInfo.bg}>
                                      {statusInfo.text}
                                    </Badge>
                                  </Col>
                                </Row>
                              </Container>
                            </Accordion.Header>
                            <Accordion.Body>
                              <h5>
                                注文詳細
                              </h5>
                              <Table bordered size="sm">
                                <tbody>
                                  <tr>
                                    <td className="bg-light" style={{ width: '150px' }}>
                                      送り状に記載する事項
                                    </td>
                                    <td style={{ whiteSpace: 'pre-wrap' }}>
                                      {invoiceDetailsText}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      送付先会社名
                                    </td>
                                    <td>
                                      {dest.shippingCompanyName || '(記載なし)'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      支店・部署名
                                    </td>
                                    <td>
                                      {`${dest.branchName || ''} ${dest.departmentName || ''}`.trim() || '(記載なし)'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      送付先氏名
                                    </td>
                                    <td>
                                      {`${dest.recipientName || ''}` || '(記載なし)'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      送付先住所
                                    </td>
                                    <td>
                                      〒{dest.postalCode} | {dest.address1}{dest.address2}{dest.address3}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      電話番号
                                    </td>
                                    <td>
                                      {dest.phoneNumber || '(記載なし)'}
                                    </td>
                                  </tr>
                                </tbody>
                              </Table>
                              {dest.trackingNumber && (
                                <>
                                  <h5 className="mt-3">配送情報</h5>
                                  <Table bordered size="sm">
                                    <tbody>
                                      <tr>
                                        <td className="bg-light" style={{ width: '150px' }}>
                                          配送業者
                                        </td>
                                        <td>
                                          {
                                            order.shippingCarrier === 'yupri' ?
                                              '日本郵便'
                                              :
                                              order.shippingCarrier === 'istar' ?
                                                '福山通運'
                                                :
                                                '不明'
                                          }
                                        </td>
                                      </tr>
                                      <tr>
                                        <td className="bg-light">
                                          お問い合わせ番号
                                        </td>
                                        <td>
                                          {/* 【変更点】trackingUrlが存在する場合のみリンクにする */}
                                          {trackingUrl ?
                                            (
                                              <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
                                                {dest.trackingNumber}
                                              </a>
                                            )
                                            :
                                            (
                                              <span>
                                                {dest.trackingNumber}
                                              </span>
                                            )
                                          }
                                          <Button
                                            variant="link"
                                            size="sm"
                                            onClick={() => navigator.clipboard.writeText(dest.trackingNumber)}
                                          >
                                            <Clipboard />
                                          </Button>
                                        </td>
                                      </tr>
                                    </tbody>
                                  </Table>
                                </>
                              )}
                              <h5 className="mt-3">
                                商品リスト
                              </h5>
                              <Table
                                striped
                                bordered
                                hover
                                responsive
                                size="sm"
                              >
                                <thead>
                                  <tr>
                                    <th>
                                      品番
                                    </th>
                                    <th>
                                      数量
                                    </th>
                                    <th>
                                      備考
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {[...order.products]
                                    .sort((a, b) => (a.partNumber || '').localeCompare(b.partNumber || ''))
                                    .map((product, pIndex) => (
                                      <tr key={pIndex}>
                                        <td>
                                          {product.partNumber}
                                        </td>
                                        <td>
                                          {product.quantity}
                                        </td>
                                        <td>
                                          {product.remarks}
                                        </td>
                                      </tr>
                                    ))}
                                </tbody>
                              </Table>
                              {/* ★ 修正: 発注者情報を表示するテーブルを完成させる */}
                              <h5 className="mt-3">
                                発注者情報
                              </h5>
                              <Table bordered size="sm">
                                <tbody>
                                  <tr>
                                    <td className="bg-light" style={{ width: '150px' }}>
                                      発注会社名
                                    </td>
                                    <td>
                                      {
                                        company ?
                                          `${company.companyPrefix || ''}${company.companyName}${company.companySuffix || ''}`
                                          :
                                          '(会社情報なし)'
                                      }
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      支店・部署名
                                    </td>
                                    <td>
                                      {
                                        company ?
                                          `${company.branchName || ''} ${company.departmentName || ''}`.trim() || '(記載なし)'
                                          :
                                          '(会社情報なし)'
                                      }
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      発注者名
                                    </td>
                                    <td>
                                      {customer?.personName || '不明'}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      発注者住所
                                    </td>
                                    <td>
                                      {
                                        customer ?
                                          `〒${customer.zipCode || ''} ${customer.address1 || ''}${customer.address2 || ''}${customer.address3 || ''}`
                                          :
                                          '(住所情報なし)'
                                      }
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="bg-light">
                                      発注者電話番号
                                    </td>
                                    <td>
                                      {customer?.phone || '(電話番号なし)'}
                                    </td>
                                  </tr>
                                </tbody>
                              </Table>
                            </Accordion.Body>
                          </Accordion.Item>
                        );
                      })}
                    </Accordion>

                    {totalPages > 1 && (
                      <CustomPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        paginate={paginate}
                      />
                    )}
                  </>
                )}
            {
              !loading &&
              filteredOrders.length === 0 &&
              <p className="text-muted">
                条件に合う発注履歴はありません。
              </p>
            }
            <hr className="my-4" />
            <Link to="/shipper-menu">
              出荷者メニューに戻る
            </Link>
          </Card.Body>
        </Card>
      </Container>
    </main>
  );
}

export default History;
